import React from 'react'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'
import { Box } from '@mui/material'
import { Form, Formik } from 'formik'
import SelectField from '@/components/formik/fields/SelectField'
import { LoadingButton } from '@mui/lab'
import * as yup from 'yup'
import TextField from '@/components/formik/fields/TextField'
import { ConnectorBleDevice, DEVICES, DEVICE_TYPE, MedconnectDeviceValue } from '@/constants/medconnect-devices-types'





type MedconnectDevicesFormProps = {
  isSubmitting: boolean
  onSubmit: (values: MedconnectDeviceValue) => void
  initialValues?: MedconnectDeviceValue
  discoveredDevices?: ConnectorBleDevice[]
  onDiscover?: () => void
  isDiscovering?: boolean
}

const deviceFormValidationSchema = yup.object().shape({
  name: yup.string().required(),
  type: yup.string().optional(),
  port: yup.string().optional(),
  baudrate: yup.number().optional().oneOf([9600, 115200]),
  receiver_name: yup.string().optional(),
  sender_name: yup.string().optional(),
  gdt_folder_path: yup.string().optional(),
  filePrefix: yup.string().optional(),
  mir_protocol: yup.string().optional().oneOf(['HL7', 'EXCHANGE']),
  mir_software_type: yup.string().optional().oneOf(['winspiroPRO', 'spiroConnect', 'MIRSpiro']),
  mir_exchange_folder: yup.string().optional(),
  mir_exchange_folder_exchange: yup.string().optional(),
  mir_program_path: yup.string().optional(),
  ecg_program_path: yup.string().optional(),
  ble_name: yup.string().optional(),
  ble_id: yup.string().optional(),
})


// shows devices that can not be access by navigator API and are present on the connector installed on the computer
export default function MedconnectDevicesForm(props: MedconnectDevicesFormProps) {

  const { isSubmitting, onSubmit, initialValues, discoveredDevices = [], onDiscover, isDiscovering = false } = props

  const { t } = useTranslation()

  const deviceOptions = [
    { value: DEVICES.PC303, label: 'PC303' },
    { value: DEVICES.PM6100, label: 'PM6100' },
    { value: DEVICES.F4, label: 'F4' },
    { value: DEVICES.SCHILLER_ECG, label: 'ECGSchiller' },
    { value: DEVICES.THERMO_TAIDOC, label: 'Thermometer' },
    { value: DEVICES.TAIDOC_BGMETER, label: "Glucometer" },
    { value: DEVICES.WELCH_ALLYN_CONNEX_SPOT_MONITOR, label: "Connex Spot Monitor" },
    { value: DEVICES.PM6100_BLE, label: 'PM6100 BLE' },
    { value: DEVICES.ECG_WATCH_FOLDER, label: "Watch a folder to automatically import PDF files" },
    { value: DEVICES.SE_1515, label: "ECG EDAN" },
    { value: DEVICES.TOUCH_ECG, label: "Touch ECG" },
    { value: DEVICES.IM3S, label: "iM3s (serial)" },
    { value: DEVICES.IM3S_BLE, label: "iM3s BLE" },
    { value: DEVICES.MIR_SPIROMETRY, label: "MIR Spirometer" },

  ]

  const typeOptions = [
    { value: DEVICE_TYPE.SERIAL, label: 'Serial' },
    { value: DEVICE_TYPE.BLE, label: 'BLE' },
  ]

  const baudRateOptions = [
    { value: 9600, label: '9600' },
    { value: 115200, label: '115200' },
  ]

  const mirProtocolOptions = [
    { value: 'HL7', label: 'HL7' },
    { value: 'EXCHANGE', label: 'eXchange' },
  ]

  const mirSoftwareTypeOptions = [
    { value: 'MIRSpiro', label: 'MIR Spiro' },
    { value: 'winspiroPRO', label: 'winspiroPRO' },
    { value: 'spiroConnect', label: 'spiroConnect' },
  ]


  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues || {
          name: DEVICES.PC303,
          type: DEVICE_TYPE.SERIAL,
          port: undefined,
          baudrate: 9600 as 9600 | 115200,
          receiver_name: undefined,
          sender_name: undefined,
          gdt_folder_path: undefined,
          ecg_folder_path: undefined,
          mir_protocol: undefined,
          mir_software_type: undefined,
          mir_exchange_folder: undefined,
          mir_exchange_folder_exchange: undefined,
          mir_program_path: undefined,
          ecg_program_path: undefined,
          ble_name: undefined,
          ble_id: undefined,
        } as MedconnectDeviceValue}
        onSubmit={onSubmit}
        validationSchema={deviceFormValidationSchema}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit}>
            <Grid container spacing={3}>
              <Grid container item spacing={4}>
                <Grid item xs={6}>
                  <SelectField fullWidth label={t('field.device.label')} name="name" options={deviceOptions} />
                </Grid>
                <Grid item xs={6}>
                  <SelectField fullWidth label={t('field.type.label')} name="type" options={typeOptions} />
                </Grid>

              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={6}>
                  <TextField fullWidth label={t('field.port.label')} name="port" placeholder={t('field.port.placeholder')} />
                </Grid>
                <Grid item xs={6}>
                  <SelectField fullWidth label={t('field.baudrate.label')} name="baudrate" options={baudRateOptions} />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={6}>
                  <TextField fullWidth label={t('field.receiverName.label')} name="receiver_name" placeholder={t('field.receiverName.placeholder')} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label={t('field.senderName.label')} name="sender_name" placeholder={t('field.senderName.placeholder')} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label={t('field.gdtFolderPath.label')} name="gdt_folder_path" placeholder={t('field.gdtFolderPath.placeholder')} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label={t('field.ecgFolderPath.label')} name="ecg_folder_path" placeholder={t('field.ecgFolderPath.placeholder')} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label={t('field.filePrefix.label')} name="filePrefix" placeholder={t('field.filePrefix.placeholder')} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label={t('field.fileSuffix.label')} name="fileSuffix" placeholder={t('field.fileSuffix.placeholder')} />
                </Grid>
              </Grid>
              {formikProps.values.name === DEVICES.SCHILLER_ECG && (
                <Grid container item spacing={4}>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.ecgProgramPath.label')} name="ecg_program_path" placeholder="C:\\Program Files (x86)\\CS-104" />
                  </Grid>
                </Grid>
              )}
              {formikProps.values.type === DEVICE_TYPE.BLE && (
                <Grid container item spacing={4}>
                  <Grid item xs={6}>
                    <SelectField
                      fullWidth
                      label={t('field.bleName.label')}
                      name="ble_id"
                      options={discoveredDevices.map((device) => ({ value: device.id, label: `${device.name}${device.rssi === null ? '' : ` (${device.rssi} dBm)`}` }))}
                      disabled={isDiscovering}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('field.bleName.placeholder')}
                      name="ble_name"
                      placeholder={t('field.bleName.placeholder')}
                    />
                  </Grid>
                  {onDiscover && <Grid item xs={12}>
                    <LoadingButton
                      color="secondary"
                      disableElevation
                      loading={isDiscovering}
                      variant="outlined"
                      type="button"
                      onClick={onDiscover}
                    >
                      {t('common:button.search.label')}
                    </LoadingButton>
                  </Grid>}
                </Grid>
              )}
              {formikProps.values.name === DEVICES.MIR_SPIROMETRY && (
                <Grid container item spacing={4}>
                  <Grid item xs={6}>
                    <SelectField fullWidth label={t('field.mirProtocol.label')} name="mir_protocol" options={mirProtocolOptions} />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectField fullWidth label={t('field.mirSoftwareType.label')} name="mir_software_type" options={mirSoftwareTypeOptions} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.mirExchangeFolder.label')} name="mir_exchange_folder" placeholder="C:\\MIR\\MirSpiro\\HL7" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.mirExchangeFolderExchange.label')} name="mir_exchange_folder_exchange" placeholder="C:\\MIR\\MirSpiro" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.mirProgramPath.label')} name="mir_program_path" placeholder="C:\\Program Files (x86)\\MIR srl Medical International Research\\MIR Spiro" />
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'end', mt: '36px' }}>
              <LoadingButton
                color="primary"
                disableElevation
                loading={isSubmitting}
                variant="contained"
                type="submit"
              >
                {t('common:button.submit.label')}
              </LoadingButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
